/*!
 * say.js — say(): words spoken, by a voice on this machine (Piper)
 * © 2026 Rob Canning — GPLv3
 *
 *     say("bro what")                         spoken now
 *     say(v1, "no shot", amp:0.8, reverb:0.4) a sound named v1, with effects
 *     say("ts is crazy", rate:1.4)            faster (0.25 .. 4)
 *     say("hello", voice:lessac)              a voice by (part of) its name
 *     say("hi", voice:vctk, speaker:12)       one of a voice's many speakers (or p239)
 *     say(a1, voice:libritts_r, speaker:irand(0, 900))   a new speaker every sentence
 *     say(a1, voice:choose(vctk, libritts_r), speaker:irand(0, 903))   a new voice too
 *     say("bro", trig:every(4, beat.hit))              on every bar
 *     say(w, reverb:0.5, voice:lessac)        no words: how w speaks from now on --
 *     text(uid:w, pick:[...], say:1)          ... each word the text shows, spoken,
 *                                             and up until the voice is done
 *
 * The server makes the speech (server/tts.js) as a WAV, once per text,
 * voice and rate, and it plays like an audio() file of that name: hush(v1),
 * level, filter:, reverb:, delay:, pan:, amp:, a box (waveform:self, or
 * viz(v1, ...)) -- by default no box. A voice says one thing at a time:
 * the next thing said by the same name cuts it off.
 *
 * Kept here: each phrase's url and length, so a word said again starts at
 * once, and a text's words are made before they are needed (prepareSpeech).
 */

import { handleAudioCue, onHush } from "./audio/audioFile.js";
import { activeAudioCues } from "./audio/voices.js";
import { fetchAudioBuffer, sharedAudioCtx, evalMaybeRandom } from "./audio/audioShared.js";
import { isRel, applyRel } from "./relative.js";
import { onReset } from "../system/teardown.js";

const AUDIO_KEYS = ["amp", "pan", "speed", "fadeIn", "fadeOut", "filter", "reverb", "delay", "waveform", "poly", "bus", "vu"];
const TOP_KEYS = ["amp", "pan", "speed", "fadeIn", "fadeOut"];

/** What a setting is before any is given: for rate:*0.9 and the like. */
const SAY_DEFAULTS = { rate: 1, speed: 1, amp: 1, pan: 0 };

const phrases = new Map();           // "voice|rate|text" -> { p: Promise<{ url, dur }>, ahead }
const settings = new Map();          // uid -> { voice, rate, amp, reverb ... }  (say(w, ...) with no words)
let hinted = false;

/**
 * The speech for `text`: its WAV's url and its length in seconds. `ahead`:
 * made before it is needed -- the server makes it after anything asked for
 * now; asked for now later, it is asked again, and moves up.
 */
export function fetchSpeech(text, voice = null, rate = 1, ahead = false, speaker = null) {
  const t = String(text ?? "").trim();
  const key = `${voice ?? ""}|${rate}|${speaker ?? ""}|${t}`;
  const had = phrases.get(key);
  if (!had || (had.ahead && !ahead && !had.done)) {
    const p = fetch("/api/tts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: t, voice, rate, ahead, speaker }),
    }).then(async r => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `tts ${r.status}`);
      return body;
    });
    const entry = { p, ahead };
    p.then(() => { entry.done = true; }, () => { if (phrases.get(key) === entry) phrases.delete(key); });   // a failure is not kept
    phrases.set(key, entry);
  }
  return phrases.get(key).p;
}

/** Say it, warning once (not every beat) when there is no voice. */
function warn(e) {
  if (hinted) return;
  hinted = true;
  console.warn(`[say] ${e.message}`);
}

/**
 * Each phrase is its own voice of the name (a1__s1, a1__s2 ...): the one
 * before is cut, the new one starts. One voice changing its file would
 * only swap it on a next loop a phrase never has -- the phrase was lost.
 * By its name, hush(a1), level, fx, vu reach them all.
 */
let phraseCount = 0;
const lastPhrase = new Map();         // uid -> the voice key saying now

function cutPhrases(uid) {
  const reg = activeAudioCues;
  for (const [key, voice] of [...reg]) {
    if (key !== uid && !key.startsWith(`${uid}__s`)) continue;
    try { voice.stop?.(0.03); } catch { /* gone */ }
    reg.delete(key);
  }
}

// uid -> how many times its speech has been silenced: a phrase made while
// it was (Piper takes a moment) is not played after
const silenced = new Map();

/**
 * What `uids` are saying cut, and no phrase still being made for them
 * starts after -- a text stopped (Ctrl+, / still(), text(uid, stop:1)).
 */
export function silenceSpeech(uids) {
  for (const uid of uids) {
    silenced.set(uid, (silenced.get(uid) || 0) + 1);
    cutPhrases(uid);
  }
}

// hush(): what it hushes says nothing still being made -- every phrase, for a
// bare hush() (the "sound" reset: a project switch and a new example too)
let hushes = 0;
onHush(uids => silenceSpeech(uids));
onReset("sound", function silenceAllSpeech() { hushes++; });
onReset("project", function forgetVoices() { settings.clear(); });   // say(w, voice:...) settings are the project's

/** Play a phrase's WAV as the sound `uid`, with the audio() args given. */
function play(uid, url, args) {
  cutPhrases(uid);
  const key = `${uid}__s${++phraseCount}`;
  lastPhrase.set(uid, key);
  const params = { uid: key, src: url, waveform: "none" };
  for (const [k, v] of Object.entries(args)) if (AUDIO_KEYS.includes(k) && v != null) params[k] = v;
  const ast = { type: "cueAudio", src: url, uid: key, loop: 1, fadeIn: 0, fadeOut: 0, toggle: false, amp: 1, params };
  for (const k of TOP_KEYS) if (params[k] != null) ast[k] = params[k];
  return handleAudioCue(ast);
}

/** speaker: as it is now -- a number, a name, or rolled for this phrase (irand(0, 900)). */
function speakerNow(s) {
  if (s.speaker == null) return null;
  const v = evalMaybeRandom(s.speaker);
  return v == null ? null : (Number.isFinite(Number(v)) ? Math.floor(Number(v)) : String(v));
}
const isRolled = v => v != null && typeof v === "object";

/** voice: as it is now -- a name, or picked for this phrase (choose(vctk, libritts_r)). */
function voiceNow(s) {
  if (s.voice == null) return null;
  const v = evalMaybeRandom(s.voice);
  return v == null ? null : String(v);
}

/** The words, the voice and the settings a say() line gives, over what its uid keeps. */
function settingsOf(uid, args) {
  return { ...(settings.get(uid) || {}), ...args };
}

/**
 * Speak `text` as the sound `uid`: returns its length in seconds once it
 * has started (0 when it could not be).
 */
export async function speak(uid, text, args = {}) {
  const s = settingsOf(uid, args);
  const was = silenced.get(uid) || 0, hushed = hushes;
  try {
    const { url, dur } = await fetchSpeech(text, voiceNow(s), s.rate ?? 1, false, speakerNow(s));
    if ((silenced.get(uid) || 0) !== was || hushes !== hushed) return 0;     // stopped while it was being made
    await play(uid, url, s);
    return dur;
  } catch (e) {
    warn(e);
    return 0;
  }
}

/**
 * Make the speech for a text's words ahead, buffers decoded, so each
 * starts on its beat: the first time a word is made takes a little while.
 */
export function prepareSpeech(uid, texts, args = {}) {
  const s = settingsOf(uid, args);
  for (const t of new Set(texts.map(x => String(x ?? "").trim()).filter(Boolean))) {
    // a voice or speaker rolled anew each time (choose, irand) cannot be made ahead
    if (isRolled(s.speaker) || isRolled(s.voice)) continue;
    fetchSpeech(t, voiceNow(s), s.rate ?? 1, true, speakerNow(s))
      .then(({ url }) => fetchAudioBuffer(sharedAudioCtx, url))
      .catch(warn);
  }
}

/**
 * text(say:1 | say:<voice>): the word shown, spoken, under the text's uid.
 * Returns a promise of its length, for the word to stay up that long.
 */
export function sayWord(text, params) {
  const voice = params.say === true || params.say === 1 || params.say === "1" ? undefined : String(params.say);
  const args = {};
  if (voice) args.voice = voice;
  if (params.sayRate != null) args.rate = params.sayRate;
  return speak(params.uid, text, args);
}

/** say(...) as a cue. */
export async function handleSayCue(ast) {
  const args = {};
  for (const a of ast?.args || []) args[a.type] = a.value;
  const uid = String(args.uid ?? "say");
  const text = args.text;
  delete args.uid; delete args.text; delete args.trig;
  // rate:*0.9, pan:+=0.2 -- from how this name speaks now (relative.js)
  const kept = settings.get(uid) || {};
  for (const [k, v] of Object.entries(args)) if (isRel(v)) args[k] = applyRel(kept[k] ?? SAY_DEFAULTS[k], v);
  if (args.pan != null) args.pan = Math.max(-1, Math.min(1, Number(args.pan)));
  // speed: bends what it is saying now as well, like tape
  if (args.speed != null && text == null) window.activeAudioCues?.get(lastPhrase.get(uid))?.update?.({ speed: Number(args.speed) });
  // no words: how this name speaks from now on (a text's say:1 too)
  if (text == null || !String(text).trim()) {
    settings.set(uid, { ...(settings.get(uid) || {}), ...args });
    return;
  }
  return speak(uid, text, args);
}
